import { defineSlotRecipe } from '@chakra-ui/react';
import { Colors, Radii, Shadows, Space, Sizes } from '../../types';

// Dimensions are plain --card-* custom properties; fallbacks are inlined in the references.
export const cardSlotRecipe = defineSlotRecipe({
  slots: ['root', 'header', 'body', 'footer', 'title', 'description'],
  base: {
    root: {
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--card-bg)',
      borderWidth: 'var(--card-border, 0)',
      borderColor: 'var(--card-border-color, transparent)',
      borderRadius: 'var(--card-radius, var(--udl-radii-md))',
      height: Sizes.FULL,
      '& > img:first-of-type, & > div:first-of-type:has(img)': {
        roundedTop: 'var(--card-radius, var(--udl-radii-md))',
        overflow: 'hidden',
      },
      '&>:last-child': {
        roundedBottom: 'var(--card-radius, var(--udl-radii-md))',
      },
    },
    header: {
      padding: 'var(--card-padding)',
      color: Colors.TEXT_EMPHASIS,
      _notFirst: { pt: 'var(--card-padding-y)' },
      _notLast: { pb: 'var(--card-padding-y)' },
    },
    body: {
      display: 'block',
      padding: 'var(--card-padding)',
      color: Colors.TEXT_MUTED,
      maxH: 'full',
      _notFirst: { pt: 'var(--card-padding-y)' },
      _notLast: { pb: 'var(--card-padding-y)' },
    },
    footer: {
      display: 'flex',
      padding: 'var(--card-padding)',
      color: Colors.TEXT_MUTED,
      justifyContent: 'end',
      gap: Space.XS,
      flexWrap: 'wrap',
      _notFirst: { pt: 'var(--card-padding-y)' },
      _notLast: { pb: 'var(--card-padding-y)' },
      _last: { roundedBottom: 'var(--card-radius, var(--udl-radii-md))' },
    },
  },
  variants: {
    variant: {
      elevated: {
        root: {
          '--card-bg': `colors.${Colors.SURFACE_PAGE}`,
          '--card-border-color': 'colors.transparent',
          outline: '1px solid',
          outlineColor: 'var(--card-border-color)',
          boxShadow: 'var(--card-shadow)',
          _dark: {
            '--card-bg': `colors.${Colors.SURFACE_ELEVATED}`,
            '--card-border-color': `colors.${Colors.BORDER_ELEVATED}`,
          },
        },
      },
      outline: {
        root: {
          '--card-bg': `colors.${Colors.SURFACE_PAGE}`,
          '--card-border-color': `colors.${Colors.BORDER_DEFAULT}`,
          '--card-border': '1px',
          '--card-shadow': 'none',
          boxShadow: Shadows.NONE,
        },
      },
      filled: {
        root: {
          '--card-bg': `colors.${Colors.SURFACE_TERTIARY}`,
          '--card-border-color': 'colors.transparent',
          '--card-shadow': 'none',
          boxShadow: Shadows.NONE,
          _dark: {
            '--card-bg': `colors.${Colors.SURFACE_TERTIARY}`,
          },
        },
      },
      unstyled: {
        root: {
          '--card-bg': `colors.${Colors.CHAKRA_BODY_BG}`,
          padding: 0,
          boxShadow: 'var(--card-shadow)',
        },
        header: { '--card-padding': '0' },
        body: { '--card-padding': '0' },
        footer: { '--card-padding': '0' },
      },
    },
    size: {
      sm: {
        root: {
          '--card-radius': `radii.${Radii.LG}`,
          '--card-padding': `spacing.${Space.SM}`,
          '--card-padding-y': `spacing.${Space['2XS']}`,
          '--card-shadow': `shadows.${Shadows.XS}`,
        },
      },
      md: {
        root: {
          '--card-radius': `radii.${Radii.XL}`,
          '--card-padding': `spacing.${Space.MD}`,
          '--card-padding-y': `spacing.${Space.XS}`,
          '--card-shadow': `shadows.${Shadows.XS}`,
        },
      },
      lg: {
        root: {
          '--card-radius': `radii.${Radii['2XL']}`,
          '--card-padding': `spacing.${Space.LG}`,
          '--card-padding-y': `spacing.${Space.XS}`,
          '--card-shadow': `shadows.${Shadows.MD}`,
        },
      },
    },
  },
  defaultVariants: {
    variant: 'elevated',
    size: 'md',
  },
});
